import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import appointmentService from '../services/appointmentService';

const statusLabels = {
  open: { label: 'Abierto', color: 'badge-info' },
  in_progress: { label: 'En proceso', color: 'badge-warning' },
  completed: { label: 'Completado', color: 'badge-success' },
  cancelled: { label: 'Cancelado', color: 'badge-danger' },
};

const activityStatusLabels = {
  pending: { label: 'Pendiente', color: 'badge-warning' },
  in_progress: { label: 'En progreso', color: 'badge-info' },
  completed: { label: 'Completado', color: 'badge-success' },
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [arAlerts, setArAlerts] = useState(null);

  useEffect(() => {
    api.get('/dashboard').then(res => setData(res.data)).finally(() => setLoading(false));
    api.get('/accounts-receivable/alerts').then(res => setArAlerts(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    appointmentService.getToday()
      .then(r => setTodayAppointments(r.data))
      .catch(() => {});
  }, []);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  const { stats, oldest_case_files, pending_docs, recent_activities, user_stats } = data;

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h4 className="mb-0">Dashboard</h4>
        <small className="text-muted">Resumen general del sistema</small>
      </div>

      {/* Tarjetas — 2 columnas en móvil, 4 en desktop */}
      <div className="row mb-4">
        {[
          {
            value: stats.active_case_files,
            label: 'Expedientes activos',
            bg: '#EEF0FB', color: '#5066E1',
            icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
          },
          {
            value: stats.stale_case_files,
            label: `Sin actividad (+${stats.stale_days}d)`,
            bg: '#FEF3C7', color: '#F59E0B',
            highlight: stats.stale_case_files > 0,
            icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
          },
          {
            value: stats.completed_case_files,
            label: 'Completados',
            bg: '#D1FAE5', color: '#10B981',
            icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
          },
          {
            value: stats.new_clients_month,
            label: 'Clientes este mes',
            bg: '#EDE9FE', color: '#7C3AED',
            icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
          },
        ].map(({ value, label, bg, color, highlight, icon }, i) => (
          <div key={i} className="col-6 col-md-3 mb-3">
            <div className="card h-100" style={{ borderLeft: highlight ? `4px solid ${color}` : undefined }}>
              <div className="card-body d-flex align-items-center">
                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3"
                  style={{ width: 48, height: 48, backgroundColor: bg, flexShrink: 0 }}>
                  <svg style={{ width: 22, height: 22, color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                  </svg>
                </div>
                <div>
                  <h2 className="mb-0 font-weight-bold" style={{ color: highlight ? color : 'inherit', fontSize: '1.5rem' }}>
                    {value}
                  </h2>
                  <small className="text-muted">{label}</small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerta expedientes lentos */}
      {stats.stale_case_files > 0 && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap mb-4" style={{ gap: '8px' }}>
          <div>
            <strong>⚠️ {stats.stale_case_files} expediente{stats.stale_case_files > 1 ? 's' : ''} sin actividad</strong>
            <span className="ml-2">— sin cambios por más de {stats.stale_days} días.</span>
          </div>
          <Link to="/case-files?stale=1" className="btn btn-sm btn-warning">Ver expedientes</Link>
        </div>
      )}

      {/* Alerta cuentas por cobrar */}
      {arAlerts?.pending_count > 0 && (
        <div
          className="alert d-flex justify-content-between align-items-center flex-wrap mb-4"
          style={{
            gap: '8px',
            backgroundColor: arAlerts.overdue_count > 0 ? '#FEE2E2' : '#FEF3C7',
            borderColor: arAlerts.overdue_count > 0 ? '#EF4444' : '#F59E0B',
            color: arAlerts.overdue_count > 0 ? '#991B1B' : '#92400E',
            border: '1px solid',
            borderRadius: '6px',
          }}>
          <div>
            <strong>
              {arAlerts.overdue_count > 0 ? '🔴' : '🟡'}{' '}
              {arAlerts.overdue_count > 0
                ? `${arAlerts.overdue_count} presupuesto${arAlerts.overdue_count !== 1 ? 's' : ''} con saldo vencido`
                : `${arAlerts.pending_count} presupuesto${arAlerts.pending_count !== 1 ? 's' : ''} con saldo pendiente`}
            </strong>
            <span className="ml-2">
              — Total por cobrar: <strong>${Number(arAlerts.total_balance).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong>
            </span>
          </div>
          <Link to="/accounts-receivable" className="btn btn-sm"
            style={{
              backgroundColor: arAlerts.overdue_count > 0 ? '#EF4444' : '#F59E0B',
              color: '#fff',
              border: 'none',
            }}>
            Ver cuentas por cobrar
          </Link>
        </div>
      )}

      <div className="row mb-4">
        {/* Gráfico de estados */}
        <div className="col-12 col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-header"><h6 className="mb-0">Estado de Expedientes</h6></div>
            <div className="card-body">
              {[
                { key: 'open', label: 'Abiertos', color: '#3B82F6' },
                { key: 'in_progress', label: 'En proceso', color: '#F59E0B' },
                { key: 'completed', label: 'Completados', color: '#10B981' },
                { key: 'cancelled', label: 'Cancelados', color: '#EF4444' },
              ].map(({ key, label, color }) => {
                const total = Object.values(stats.by_status).reduce((a, b) => a + b, 0) || 1;
                const pct = Math.round((stats.by_status[key] / total) * 100);
                return (
                  <div key={key} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>{label}</small>
                      <small><strong>{stats.by_status[key]}</strong> ({pct}%)</small>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div className="progress-bar" style={{ width: `${pct}%`, backgroundColor: color }}></div>
                    </div>
                  </div>
                );
              })}
              <div className="mt-3 pt-2 border-top">
                <div className="d-flex justify-content-between">
                  <small className="text-muted">Nuevos este mes</small>
                  <span className="badge badge-primary">{stats.new_case_files_month}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expedientes más viejos */}
        <div className="col-12 col-md-8 mb-3">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Expedientes con más tiempo abiertos</h6>
              <Link to="/case-files" className="btn btn-sm btn-outline-primary">Ver todos</Link>
            </div>
            <div className="card-body p-0 mobile-card-list">
              {oldest_case_files.length === 0 ? (
                <div className="text-center text-muted py-4">No hay expedientes activos</div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}></th>
                      <th>Cliente / Expediente</th>
                      <th className="mobile-hide">Responsable</th>
                      <th className="mobile-hide">Docs</th>
                      <th>Tiempo</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldest_case_files.map(cf => (
                      <tr key={cf.id} style={{ backgroundColor: cf.is_stale ? '#FFF8F0' : 'inherit' }}>
                        <td style={{ width: 30 }} className="td-alert">
                          {cf.is_stale && <span title="Sin actividad reciente">⚠️</span>}
                        </td>
                        <td className="td-name">
                          <Link to={`/clients/${cf.client_id}`} className="text-primary d-block">
                            {cf.client?.name}
                          </Link>
                          <Link to={`/case-files/${cf.id}`} className="text-dark" style={{ fontSize: '12px' }}>
                            {cf.title}
                          </Link>
                        </td>
                        <td className="mobile-hide">{cf.assigned_user?.name}</td>
                        <td className="mobile-hide">
                          <span className={`badge ${cf.document_stats?.delivered === cf.document_stats?.total && cf.document_stats?.total > 0 ? 'badge-success' : 'badge-warning'}`}>
                            {cf.document_stats?.delivered}/{cf.document_stats?.total}
                          </span>
                        </td>
                        <td data-label="Tiempo">
                          <span className={`badge ${cf.is_stale ? 'badge-danger' : 'badge-secondary'}`}>
                            {cf.elapsed_time}
                          </span>
                        </td>
                        <td data-label="Estado">
                          <span className={`badge ${statusLabels[cf.status]?.color}`}>
                            {statusLabels[cf.status]?.label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Carga de trabajo por usuario */}
      {user_stats && user_stats.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Carga de trabajo por usuario</h6>
          </div>
          <div className="card-body p-0 mobile-card-list">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th className="mobile-hide">Rol</th>
                  <th>Activos</th>
                  <th>Sin act.</th>
                  <th className="mobile-hide">Completados</th>
                  <th className="mobile-hide">Carga</th>
                </tr>
              </thead>
              <tbody>
                {user_stats.map(u => {
                  const total = u.active_case_files + u.completed_case_files || 1;
                  const pct = Math.round((u.active_case_files / total) * 100);
                  const roleLabels = { notary: 'Notario', supervisor: 'Supervisor', staff: 'Staff' };
                  const roleColors = { notary: 'badge-primary', supervisor: 'badge-info', staff: 'badge-secondary' };
                  return (
                    <tr key={u.id}>
                      <td className="td-name">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%', backgroundColor: '#EEF0FB',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 'bold', color: '#5066E1', fontSize: 13, flexShrink: 0
                          }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <Link to={`/case-files?assigned_user_id=${u.id}`} className="font-weight-bold">
                            {u.name}
                          </Link>
                        </div>
                        <div className="d-md-none mt-1">
                          <span className={`badge ${roleColors[u.role]}`}>{roleLabels[u.role]}</span>
                        </div>
                      </td>
                      <td className="mobile-hide">
                        <span className={`badge ${roleColors[u.role]}`}>{roleLabels[u.role]}</span>
                      </td>
                      <td data-label="Activos">
                        <span className="badge badge-warning">{u.active_case_files}</span>
                      </td>
                      <td data-label="Sin act.">
                        {u.stale_case_files > 0
                          ? <span className="badge badge-danger">⚠️ {u.stale_case_files}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                      <td className="mobile-hide">
                        <span className="badge badge-success">{u.completed_case_files}</span>
                      </td>
                      <td className="mobile-hide" style={{ width: '180px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="progress flex-grow-1" style={{ height: '8px' }}>
                            <div className="progress-bar" style={{
                              width: `${pct}%`,
                              backgroundColor: u.stale_case_files > 0 ? '#EF4444' : '#5066E1'
                            }}></div>
                          </div>
                          <small className="text-muted">{u.active_case_files}</small>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pendientes y actividades recientes */}
      <div className="row">
        <div className="col-12 col-md-4 mb-3">
          <div className="card h-100">
            <div className="card-header"><h6 className="mb-0">Documentos Obligatorios Pendientes</h6></div>
            <div className="card-body p-0">
              {pending_docs.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <div style={{ fontSize: '2rem' }}>✅</div>
                  <p className="mt-2">Todo al día</p>
                </div>
              ) : (
                <ul className="list-group list-group-flush">
                  {pending_docs.map((doc, i) => (
                    <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <Link to={`/case-files/${doc.case_file_id}`} className="text-dark font-weight-bold">
                          {doc.case_file_title}
                        </Link>
                        <br />
                        <small className="text-muted">{doc.client_name}</small>
                      </div>
                      <span className="badge badge-danger badge-pill">{doc.pending_count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Actividades recientes */}
        <div className="col-12 col-md-8 mb-3">
          <div className="card h-100">
            <div className="card-header"><h6 className="mb-0">Actividades Recientes</h6></div>
            <div className="card-body p-0 mobile-card-list">
              {recent_activities.length === 0 ? (
                <div className="text-center text-muted py-4">No hay actividades recientes</div>
              ) : (
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Actividad</th>
                      <th>Expediente</th>
                      <th className="mobile-hide">Responsable</th>
                      <th className="mobile-hide">Tiempo</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_activities.map(activity => (
                      <tr key={activity.id}>
                        <td className="td-name">
                          <div>{activity.title}</div>
                          <span className={`badge ${activity.activity_type?.color || 'badge-secondary'}`}>
                            {activity.activity_type?.name}
                          </span>
                        </td>
                        <td>
                          <Link to={`/case-files/${activity.case_file_id}`} className="d-block">
                            {activity.case_file?.title}
                          </Link>
                          <small className="text-muted">{activity.case_file?.client?.name}</small>
                        </td>
                        <td className="mobile-hide">{activity.user?.name}</td>
                        <td className="mobile-hide">
                          <small className="text-muted">{activity.elapsed_time}</small>
                        </td>
                        <td data-label="Estado">
                          <span className={`badge ${activityStatusLabels[activity.status]?.color}`}>
                            {activityStatusLabels[activity.status]?.label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Widget Citas de hoy */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="mb-0">📅 Citas de hoy</h6>
          <a href="/agenda" className="btn btn-sm btn-outline-primary">Ver agenda completa</a>
        </div>
        <div className="card-body p-0">
          {todayAppointments.length === 0 ? (
            <div className="text-center text-muted py-3">No hay citas programadas para hoy</div>
          ) : (
            <ul className="list-group list-group-flush">
              {todayAppointments.map(a => {
                const statusColors = {
                  programada: '#3b82f6', confirmada: '#10b981',
                  realizada: '#6b7280', cancelada: '#ef4444', reprogramada: '#f59e0b'
                };
                const start = new Date(a.start_datetime);
                return (
                  <li key={a.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div style={{ borderLeft: `4px solid ${statusColors[a.status]}`, paddingLeft: 10 }}>
                      <div><strong>{a.title}</strong></div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {start.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        {a.client ? ` · ${a.client.name}` : ''}
                        {a.user ? ` · ${a.user.name}` : ''}
                      </div>
                    </div>
                    <a href={`/agenda/${a.id}`} className="btn btn-sm btn-outline-secondary">Ver</a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;